"use client";

import {
	RiCheckLine,
	RiExpandUpDownLine,
	RiMusic2Line,
} from "@remixicon/react";
import { getGenreDisplayName } from "@workspace/shared-utils";
import { Button } from "@workspace/ui/components/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

export interface ShowGenreFilterProps {
	value: string[];
	onChange: (value: string[]) => void;
	availableGenres: string[];
	className?: string;
}

export function ShowGenreFilter({
	value,
	onChange,
	availableGenres,
	className,
}: ShowGenreFilterProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const getDisplayName = () => {
		if (value.length === 0) return "All Genres";
		if (value.length === 1) return getGenreDisplayName(value[0]!);
		return `${value.length} Genres`;
	};

	const handleSelect = useCallback(
		(genre: string) => {
			const newValue = value.includes(genre)
				? value.filter((g) => g !== genre)
				: [...value, genre];
			onChange(newValue);
		},
		[onChange, value],
	);

	const handleClearAll = useCallback(() => {
		onChange([]);
		setOpen(false);
	}, [onChange]);

	// Memoize filtered genres - alphabetical order
	const sortedGenres = useMemo(() => {
		return availableGenres
			.filter((g) =>
				getGenreDisplayName(g).toLowerCase().includes(search.toLowerCase())
			)
			.sort();
	}, [availableGenres, search]);

	return (
		<Popover
			open={open}
			onOpenChange={(isOpen) => {
				setOpen(isOpen);
				if (!isOpen) setSearch("");
			}}
		>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn("h-10 w-full justify-between px-3", className)}
				>
					<div className="flex min-w-0 items-center gap-2">
						<RiMusic2Line className="h-4 w-4 shrink-0" />
						<span className="truncate">{getDisplayName()}</span>
					</div>
					<RiExpandUpDownLine className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[var(--radix-popover-trigger-width)] p-0"
				side="bottom"
				align="start"
				sideOffset={4}
				collisionPadding={8}
			>
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Search genres..."
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList className="max-h-[200px]">
						<CommandEmpty>No genres found.</CommandEmpty>
						<CommandGroup>
							<CommandItem onSelect={handleClearAll}>
								<div className="flex items-center justify-between w-full">
									<span className="truncate">All Genres</span>
									<RiCheckLine
										className={cn(
											"ml-2 h-4 w-4 shrink-0",
											value.length === 0 ? "opacity-100" : "opacity-0",
										)}
									/>
								</div>
							</CommandItem>
							{value.length > 0 && (
								<CommandItem
									value="clear-selection"
									onSelect={handleClearAll}
									className="text-destructive"
								>
									<span>Clear Selection</span>
								</CommandItem>
							)}
							{sortedGenres.map((genre) => {
								const isSelected = value.includes(genre);
								return (
									<CommandItem
										key={genre}
										value={genre}
										onSelect={() => handleSelect(genre)}
									>
										<div className="flex items-center justify-between w-full">
											<span className="truncate">{getGenreDisplayName(genre)}</span>
											<RiCheckLine
												className={cn(
													"ml-2 h-4 w-4 shrink-0",
													isSelected ? "opacity-100" : "opacity-0",
												)}
											/>
										</div>
									</CommandItem>
								);
							})}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
